import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { Bell, MailCheck } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Notifications() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function markRead(id) {
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/notifications/read/${id}`);
    } catch (err) {
      console.warn("Failed to mark notification read:", err);
    }
  }

  async function markAllRead() {
    const unread = items.filter((n) => !n.read);
    setItems((xs) => xs.map((n) => ({ ...n, read: true })));
    await Promise.all(
      unread.map((n) =>
        api.post(`/notifications/read/${n.id}`).catch((err) =>
          console.warn("markAllRead failed for", n.id, err),
        ),
      ),
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-adult font-bold text-3xl text-slate-900 flex items-center gap-2">
              <Bell className="w-7 h-7" /> {t("nav.notifications")}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{t("notifications.subtitle")}</p>
          </div>
          {items.some((n) => !n.read) && (
            <Button variant="outline" onClick={markAllRead} data-testid="notif-mark-all">
              <MailCheck className="w-4 h-4 mr-1" /> {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
        {loading && <div className="text-slate-500 text-sm">{t("common.loading")}</div>}
        {!loading && items.length === 0 && (
          <div className="hb-card p-10 text-center">
            <Bell className="w-10 h-10 mx-auto text-slate-400" />
            <div className="mt-3 font-semibold text-slate-900">{t("notifications.empty")}</div>
            <p className="text-sm text-slate-600 mt-1">{t("notifications.emptyBody")}</p>
          </div>
        )}
        <div className="space-y-2">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => markRead(n.id)}
              data-testid={`notif-page-${n.id}`}
              className={`w-full text-left hb-card p-4 hover:bg-slate-50 transition-colors ${n.read ? "opacity-70" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 inline-block" />}
                  {n.title}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">{n.category}</span>
              </div>
              <div className="text-sm text-slate-600 mt-1">{n.body}</div>
              <div className="text-[11px] text-slate-400 mt-1">
                {new Date(n.created_at).toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
